import { IS_BROWSER } from 'src/utils/application'
import {
  convertToBoolean,
  signUp,
  account,
  getLiveEventStatus,
  checkMysiteOrPersonalOffer,
  i18nTranslate,
} from 'src/utils'
import { isEquinoxMysite, isEquinoxPOSite } from 'src/utils/mySiteUtils'
import { getAppConfig } from 'config/appConfig'
import { cartContainer, customerContainer } from 'src/models'
import {
  signInwithRedirect,
  signOutWithRedirect,
} from 'src/utils/signInUtils'
import {
  HEADER_AUTH_UI_I18N,
  HEADER_CN_HOME_COUNTRY,
  IS_AUTHENTICATED_STORAGE_KEY,
  IS_AUTHENTICATED_STORAGE_VALUE,
  SSR_RESOURCE,
} from './headerAuthService.constants'

export { HEADER_MFE_APP_NAME } from './headerAuthService.constants'

/** Auth handlers for header-mfe (`params.authService`). */
export function createHeaderAuthService(history) {
  return createHeaderAuthServiceWithGetter(() => history)
}

function createHeaderAuthServiceWithGetter(getHistory) {
  const navigateToSignIn = () => signInwithRedirect()
  const navigateToSignUp = () => signUp(getHistory())
  const navigateToAccount = () => account(getHistory())

  return {
    navigateToSignIn,
    navigateToSignUp,
    navigateToAccount,
    signOut: () => {
      syncHeaderOktaAuthenticatedFlag(false)
      return signOutWithRedirect()
    },
  }
}

/** Stable authService for federation params (same object identity each render). */
const headerAuthHistoryRef = { current: null }
let stableHeaderAuthService = null

function getStableHeaderAuthService(history) {
  headerAuthHistoryRef.current = history
  if (!stableHeaderAuthService) {
    stableHeaderAuthService = createHeaderAuthServiceWithGetter(
      () => headerAuthHistoryRef.current
    )
  }
  return stableHeaderAuthService
}

/** @internal test helper */
export function resetStableHeaderAuthServiceForTests() {
  stableHeaderAuthService = null
  headerAuthHistoryRef.current = null
}

function getLegacyQuickLinksContextValue() {
  return isEquinoxMysite() || isEquinoxPOSite()
    ? ''
    : checkMysiteOrPersonalOffer()
}

export function isHeaderLiveEventEnabled() {
  return convertToBoolean(getLiveEventStatus())
}

/**
 * Same gate as UserMenu.registerUserOktaComponent: skip QuickLinks on
 * non-Equinox mysite / personal offer. Equinox mysite/PO keeps QuickLinks.
 */
export function isHeaderQuickLinksEnabled() {
  return (
    convertToBoolean(getAppConfig()?.enableQuickLinks) &&
    !getLegacyQuickLinksContextValue()
  )
}

/** Same contact + referenceAttribute + !guest check as UserMenu Okta path. */
export function isHeaderOktaRegisteredUser(profileResponse) {
  const profile = profileResponse || {}
  const contact =
    profile.email ||
    profile.phoneNumber ||
    profile.userName ||
    profile.userrole
  return Boolean(
    contact && profile.referenceAttribute != null && !profile.guest
  )
}

export function isHeaderCustomerRegistered(profileResponse, isRegisterUser) {
  return (
    isHeaderOktaRegisteredUser(profileResponse) || isRegisterUser === true
  )
}

export function isHeaderCnEventAccountDisabled() {
  if (isHeaderLiveEventEnabled()) return false
  return (
    customerContainer?.getUserHomeCountry?.() === HEADER_CN_HOME_COUNTRY
  )
}

function translateHeaderAuthLabel({ key, fallback }) {
  return i18nTranslate(key, fallback, SSR_RESOURCE)
}

export function buildHeaderAuthUi() {
  return {
    hideGuestSignup:
      isHeaderLiveEventEnabled() ||
      customerContainer?.disableFeaturesForRPReview?.() === true,
    disableAccountNavigate: isHeaderCnEventAccountDisabled(),
    disableSignTextMobileHeader: convertToBoolean(
      getAppConfig()?.disableSignTextMobileHeader
    ),
    welcomeLabel: translateHeaderAuthLabel(HEADER_AUTH_UI_I18N.welcome),
    signInLabel: translateHeaderAuthLabel(HEADER_AUTH_UI_I18N.signIn),
    signUpLabel: translateHeaderAuthLabel(HEADER_AUTH_UI_I18N.signUp),
    signOutLabel: translateHeaderAuthLabel(HEADER_AUTH_UI_I18N.signOut),
    myAccountLabel: translateHeaderAuthLabel(HEADER_AUTH_UI_I18N.myAccount),
  }
}

function canUseLocalStorage() {
  return IS_BROWSER && typeof localStorage !== 'undefined'
}

/**
 * Keep the UserMenu Okta `IsAuthenticated` flag in sync with session.
 * Write on registered; remove on guest / after sign-out so the next load
 * does not treat a leftover `'true'` as still logged in.
 */
export function syncHeaderOktaAuthenticatedFlag(isRegistered) {
  if (!canUseLocalStorage()) {
    return
  }
  try {
    if (!isRegistered) {
      localStorage.removeItem(IS_AUTHENTICATED_STORAGE_KEY)
      return
    }
    // getItem returns a string; compare to 'true' so we do not rewrite every render.
    if (
      localStorage.getItem(IS_AUTHENTICATED_STORAGE_KEY) !==
      IS_AUTHENTICATED_STORAGE_VALUE
    ) {
      localStorage.setItem(
        IS_AUTHENTICATED_STORAGE_KEY,
        IS_AUTHENTICATED_STORAGE_VALUE
      )
    }
  } catch {
    // Safari private / locked storage must not block header params or sign-out.
  }
}

export function getHeaderMfeServerParams() {
  const profileResponse = customerContainer?.profileResponse || {}
  const isCustomerRegistered = isHeaderCustomerRegistered(
    profileResponse,
    customerContainer?.isRegisterUser
  )

  return {
    isLoadFromMfe: true,
    customerData: profileResponse,
    isCustomerRegistered,
    cartCount: cartContainer?.cartResponse?.value?.count || 0,
    enableQuickLinks: isHeaderQuickLinksEnabled(),
    enableLiveEvent: isHeaderLiveEventEnabled(),
    authUi: buildHeaderAuthUi(),
  }
}

export function getHeaderMfeParams(history) {
  const params = getHeaderMfeServerParams()
  if (!IS_BROWSER) {
    return params
  }
  syncHeaderOktaAuthenticatedFlag(params.isCustomerRegistered)
  return {
    ...params,
    routerHistory: history,
    authService: getStableHeaderAuthService(history),
  }
}
